/**
 * Vault Hire shell module map.
 *
 * Single source of truth for which modules each role sees in the top-bar
 * dropdown and the left-rail sub-sections. Adding a new module = adding
 * one entry here.
 *
 * Industry portability: the visual chrome is stable; only the per-module
 * `industryPreset` field (used by the candidate vault tab list etc) varies.
 * The default "banking" preset ships with the UK FCA SMCR/RTW/SYSC tab set.
 */
import {
  Lock, Briefcase, MessageSquare, LayoutDashboard, Users, Search, Workflow,
  ClipboardList, Inbox, ShieldCheck, FileSearch, Building2, Settings, FileText,
  CalendarClock, BadgeCheck,
} from 'lucide-react';

/**
 * @typedef {Object} ShellModule
 * @property {string} id            - URL prefix this module is "active" for
 * @property {string} label         - Top-bar dropdown + left-rail title
 * @property {string} icon          - lucide-react component name (stringly typed for portability)
 * @property {string} path          - Default landing route
 * @property {Array<{id:string,label:string,path:string,icon:string}>} sections - Left-rail items
 */

const MOD = {
  // CANDIDATE ---------------------------------------------------------------
  myVault: {
    id: 'my-vault',
    label: 'My Vault',
    Icon: Lock,
    path: '/candidate/dashboard',
    sections: [
      { id: 'overview',     label: 'Overview',     path: '/candidate/dashboard',         Icon: LayoutDashboard },
      { id: 'documents',    label: 'Documents',    path: '/candidate/dashboard#documents', Icon: FileText },
      { id: 'applications', label: 'Applications', path: '/candidate/applications',      Icon: Briefcase },
      { id: 'audit',        label: 'Audit trail',  path: '/candidate/dashboard#audit',   Icon: FileSearch },
    ],
  },
  candidateApplications: {
    id: 'applications',
    label: 'Applications',
    Icon: Briefcase,
    path: '/candidate/applications',
    sections: [],
  },

  // RECRUITER --------------------------------------------------------------
  recruiterHQ: {
    id: 'recruiter-hq',
    label: 'Recruiter HQ',
    Icon: LayoutDashboard,
    path: '/recruiter',
    sections: [
      { id: 'hq',         label: 'Recruiter HQ',     path: '/recruiter',          Icon: LayoutDashboard },
      { id: 'jobs',       label: 'Jobs',             path: '/employer/dashboard', Icon: Briefcase },
      { id: 'candidates', label: 'Candidate search', path: '/employer/dashboard#candidates', Icon: Search },
      { id: 'pipelines',  label: 'Pipelines',        path: '/cockpit',            Icon: Workflow },
    ],
  },

  // HIRING MANAGER ---------------------------------------------------------
  hiringManager: {
    id: 'hiring-manager',
    label: 'My Reqs',
    Icon: Users,
    path: '/hiring-manager',
    sections: [
      { id: 'reqs',       label: 'My Reqs',    path: '/hiring-manager',           Icon: Users },
      { id: 'reviews',    label: 'Reviews',    path: '/hiring-manager#reviews',   Icon: BadgeCheck },
      { id: 'interviews', label: 'Interviews', path: '/hiring-manager#interviews', Icon: CalendarClock },
    ],
  },

  // ADMIN / OWNER ----------------------------------------------------------
  pm: {
    id: 'pm',
    label: 'Implementation PM',
    Icon: ClipboardList,
    path: '/admin/pm',
    sections: [
      { id: 'projects', label: 'All projects',     path: '/admin/pm',       Icon: ClipboardList },
      { id: 'inbox',    label: 'Portfolio inbox',  path: '/admin/pm/inbox', Icon: Inbox },
    ],
  },
  compliance: {
    id: 'compliance',
    label: 'Compliance centre',
    Icon: ShieldCheck,
    path: '/admin/compliance',
    sections: [
      { id: 'admin', label: 'Admin', path: '/admin/compliance', Icon: ShieldCheck },
    ],
  },
  investor: {
    id: 'investor',
    label: 'Investor room',
    Icon: Building2,
    path: '/admin/investor',
    sections: [],
  },
  adminDashboard: {
    id: 'admin',
    label: 'Admin',
    Icon: Settings,
    path: '/admin/dashboard',
    sections: [
      { id: 'dashboard', label: 'Dashboard',     path: '/admin/dashboard', Icon: LayoutDashboard },
      { id: 'users',     label: 'Users',         path: '/admin/dashboard#users', Icon: Users },
      { id: 'settings',  label: 'Settings',      path: '/admin/dashboard#settings', Icon: Settings },
    ],
  },

  // COMMON ----------------------------------------------------------------
  messages: {
    id: 'messages',
    label: 'Messages',
    Icon: MessageSquare,
    path: '/messages',
    sections: [],
  },
};

/** Role → ordered list of modules shown in the top-bar dropdown. */
export const MODULES_BY_ROLE = {
  candidate:       [MOD.myVault, MOD.candidateApplications, MOD.messages],
  employer:        [MOD.recruiterHQ, MOD.messages],
  recruiter:       [MOD.recruiterHQ, MOD.messages],
  hiring_manager:  [MOD.hiringManager, MOD.messages],
  admin:           [MOD.pm, MOD.compliance, MOD.investor, MOD.adminDashboard, MOD.messages],
  owner:           [MOD.pm, MOD.compliance, MOD.investor, MOD.adminDashboard, MOD.messages],
};

/** Resolve the currently-active module from the URL path. */
export function activeModuleForPath(role, pathname) {
  const list = MODULES_BY_ROLE[role] || [];
  // Best-match: longest base path that the pathname starts with
  let best = null;
  let bestLen = -1;
  for (const m of list) {
    const base = m.path.split('#')[0];
    if (pathname === base || pathname.startsWith(base + '/')) {
      if (base.length > bestLen) { best = m; bestLen = base.length; }
    }
  }
  return best || list[0] || null;
}
